import '../scss/booking.scss';
import React from 'react';
import { createRoot } from 'react-dom/client';
import SearchWrapper from './components/Map/SearchWrapper';
import AppointmentsWrapper from './components/Appointments/AppointmentsWrapper';
import SummaryBox from './components/Summary/SummaryBox';

class BookingJourney {
    constructor() {
        window.dataLayer = window.dataLayer || [];
        this.instructionsPage = 3;
        this.yellowFeverPage = 2;
        this.mapPageNumber = 6;
        this.appointmentsPageNumber = 7;
        this.lastPage = 9;
        this.mapSelectedClinicFieldID = 208;
        this.clinicIsSetFieldID = 194;

        this.totalPeople = 0;
        this.countries = [];
        this.myServices = '';
        this.formID = '';
        this.selectedAppointment = [];
        this.selectedClinic = '';
        this.selectedAddress = {};
        this.selectedRadius = 50;

        this.analytics = {
            step_number: '',
            step_name: '',
            service: undefined,
            service_type: undefined,
            destination: undefined,
            clinic: undefined,
            type: undefined,
            number_of_patients: undefined,
            date: undefined,
        };

        this.triggerAnalytics(1, 'Choose Service');
        this.loadCountries();
        this.listenToNewPage();
        this.listenToReact();
        this.unloadPopup();
        this.hideFirstNextButton();

        const searchParams = new URLSearchParams(window.location.search);
        this.clinicIsSet = searchParams.has('clinicID');
        const clinicID = this.clinicIsSet
            ? searchParams.get('clinicID')
            : false;

        if (clinicID) {
            this.fetchClinicData(clinicID);
        }
    }

    // events coming from the react components
    listenToReact = () => {
        // listen to selected date and add to hidden input (is json with other required data - resource, treatment and practitioner)
        document.addEventListener('select-date', (e) => {
            const pageID = `gform_page_${this.formID}_${this.appointmentsPageNumber}`;
            const page = document.getElementById(pageID);
            const hidden = page.querySelector('input.gform_hidden');
            hidden.value = e.detail;
            this.selectedAppointment = JSON.parse(e.detail);
            const button = page.querySelector('.gform_next_button');
            button.classList.remove('u-opacity');
        });

        // listen to selected clinic and add to hidden input
        document.addEventListener('select-clinic', (e) => {
            const fieldID = `input_${this.formID}_${this.mapSelectedClinicFieldID}`;
            const field = document.getElementById(fieldID);
            field.value = e.detail.id;
            this.selectedClinic = e.detail;

            const pageID = `gform_page_${this.formID}_${this.mapPageNumber}`;
            const page = document.getElementById(pageID);
            const button = page.querySelector('.gform_next_button');
            button.classList.remove('u-opacity');
            setTimeout(() => {
                button.click();
            }, 500);
        });

        document.addEventListener('select-location', (e) => {
            this.selectedAddress = e.detail;
        });

        document.addEventListener('select-radius', (e) => {
            this.selectedRadius = e.detail;
        });
    };

    // triggered when a new page is rendered, triggered from gform_post_render which triggers in custom form code using Code Chest
    // using this method because I wanted a page number
    listenToNewPage = () => {
        document.addEventListener('next-page', (e) => {
            if (!this.steps) {
                this.steps = document.querySelector(
                    '[data-behaviour="steps-indicator"]',
                );
            }
            const page = Number(e.detail.current_page);
            this.formID = e.detail.form_id;
            if (!this.clinicBox) {
                this.setupClinicBox();
            }
            this.showHideClinicBox(page);

            if (page < this.mapPageNumber) {
                this.steps.setAttribute('data-active', null);
                // note this should only happen when the user has returned back to the first page
                if (page === 1) {
                    this.hideFirstNextButton();
                    this.resetCheckboxes();
                    this.triggerAnalytics(1, 'Choose Service');
                } else if (page == this.instructionsPage) {
                    this.triggerAnalytics(2, 'Get Started');
                    this.checkYellowFever();
                    setTimeout(() => {
                        this.moveFootnote();
                    }, 500);
                } else if (page == this.yellowFeverPage) {
                    this.yellowFeverSetup();
                } else {
                    this.triggerAnalytics(3, 'Choose Service Type');
                }
            } else if (page === this.mapPageNumber) {
                this.recordSelectedServices();
                this.triggerAnalytics(4, 'Find Your Clinic');
                this.steps.setAttribute('data-active', 'one');
                this.renderMap();
            } else if (page === this.appointmentsPageNumber) {
                if (!this.myServices) {
                    this.recordSelectedServices();
                }
                this.triggerAnalytics(5, 'Select Appointment');
                this.steps.setAttribute('data-active', 'two');
                this.renderAppointments();
            } else if (page === this.lastPage) {
                this.triggerAnalytics(7, 'Personal Details');
                this.steps.setAttribute('data-active', 'three');
                this.hideConfirmOnClick();
                this.renderSummary();
                this.countrySelect();
                this.wrapElements(
                    '.personal-start',
                    '.personal-end',
                    'personal-details',
                );
                this.wrapElements(
                    '.contact-start',
                    '.contact-end',
                    'contact-details',
                );
                this.wrapElements(
                    '.additional-start',
                    '.additional-end',
                    'additional-details',
                );
            }
        });
    };

    // check value of selected services
    recordSelectedServices = () => {
        let services = document.querySelectorAll(
            '.sexual-health input:checked, .generic-vaccinations input:checked',
        );
        // if no services are selected, check yellow fever
        if (!services.length) {
            services = document.querySelectorAll('.yellow-fever input:checked');
            // if the user has not selected yellow fever, clear this again so that it fetches the response from the first question
            if (
                services.length &&
                (services[0].value == 'no' || services[0].value == 'unsure')
            ) {
                services = [];
            }
        }
        // if there is still no services selected, check the first and second question
        if (!services.length) {
            services = document.querySelectorAll(
                '.first-question input:checked, .second-question input:checked',
            );
        }

        const oldServices = this.myServices;
        this.myServices = '';
        services.forEach((opt) => {
            if (opt.value !== 'void') {
                this.myServices += opt.value + '-';
            }
        });
        if (!this.clinicIsSet && oldServices !== this.myServices) {
            this.selectedClinic = '';
        }
    };

    // render the react map component
    renderMap = () => {
        setTimeout(() => {
            const map = document.querySelector(
                '[data-behaviour="booking-map"]',
            );
            if (map) {
                const root = createRoot(map);
                root.render(
                    <SearchWrapper
                        defaultAddressValue={this.selectedAddress}
                        defaultRadiusValue={this.selectedRadius}
                        services={this.myServices}
                        selectedClinic={this.selectedClinic.id}
                    />,
                );

                if (!this.selectedClinic) {
                    const button = map
                        .closest('.gform_page')
                        .querySelector('.gform_next_button');
                    button.classList.add('u-opacity');
                }
            }
        }, 500);
    };

    // render the react summary component
    renderSummary = () => {
        setTimeout(() => {
            const summary = document.querySelector(
                '[data-behaviour="booking-summary"]',
            );
            if (summary) {
                const root = createRoot(summary);
                root.render(
                    <SummaryBox
                        selectedClinic={this.selectedClinic.id}
                        services={this.myServices}
                        people={this.totalPeople}
                        selectedAppointment={this.selectedAppointment}
                    />,
                );
            }
        }, 500);
    };

    // render the react appointments component
    renderAppointments = () => {
        // keep track of the number of people selected
        const pageID = `gform_page_${this.formID}_${this.appointmentsPageNumber}`;
        const people = document
            .getElementById(pageID)
            .querySelectorAll('.number-adults select,.number-children select');
        this.totalPeople = 0;
        people.forEach((input) => {
            this.totalPeople += Number(input.value);
            input.addEventListener('change', () => {
                this.totalPeople = 0;
                people.forEach((input) => {
                    this.totalPeople += Number(input.value);
                });

                const appointments = document.querySelector(
                    '[data-behaviour="booking-appointments"]',
                );
                const button = appointments
                    .closest('.gform_page')
                    .querySelector('.gform_next_button');
                button.classList.add('u-opacity');

                document.dispatchEvent(
                    new CustomEvent('select-people', {
                        detail: this.totalPeople,
                    }),
                );
            });
        });

        // render the appointments
        setTimeout(() => {
            const appointments = document.querySelector(
                '[data-behaviour="booking-appointments"]',
            );
            if (appointments) {
                const root = createRoot(appointments);
                root.render(
                    <AppointmentsWrapper
                        selectedClinic={this.selectedClinic.id}
                        services={this.myServices}
                        defaultPeople={this.totalPeople}
                    />,
                );
                const button = appointments
                    .closest('.gform_page')
                    .querySelector('.gform_next_button');
                button.classList.add('u-opacity');
            }
        }, 500);
    };

    // if the user has come back to the first screen, need to clear choices
    resetCheckboxes = () => {
        const boxes = document.querySelectorAll(
            '.first-question input, .second-question input',
        );
        boxes.forEach((box) => {
            // on select of a new option
            box.addEventListener('change', () => {
                // clear out selected services
                this.myServices = '';
                let services = document.querySelectorAll(
                    '.yellow-fever input:checked, .sexual-health input:checked, .generic-vaccinations input:checked',
                );
                services.forEach((opt) => {
                    opt.checked = false;
                });
                // see if this option is in the first question or second question, and set a var to the opposite value
                const toggle = box
                    .closest('.gfield')
                    .classList.contains('first-question')
                    ? '.second-question'
                    : '.first-question';
                // uncheck anything that is checked in the other question
                const otherChecked = document.querySelector(
                    toggle + ' input:checked',
                );
                if (otherChecked && otherChecked.value !== 'void') {
                    otherChecked.checked = false;
                }
            });
        });
    };

    loadCountries = () => {
        fetch('/cd/feeds/booking-journey/destinations')
            .then((response) => response.json())
            .then((data) => {
                this.countries = data.destinations;
            })
            .catch((error) => {
                alert('Could not load countries');
            });
    };

    // country select autocomplete
    countrySelect = () => {
        const isTravel = document.querySelector(
            '.first-question input:checked',
        )?.value;
        if (isTravel === 'void') {
            return;
        }
        const autoCompleteJS = new autoComplete({
            selector: '.select-country input',
            placeHolder: 'Search for a country',
            data: {
                src: this.countries,
                keys: ['title'],
            },
            resultItem: {
                highlight: true,
            },
            events: {
                input: {
                    selection: (event) => {
                        const selection = event.detail.selection.value;
                        autoCompleteJS.input.value = selection.title;
                    },
                },
            },
        });
    };

    // when the user clicks off the page
    unloadPopup = () => {
        window.addEventListener('beforeunload', (e) => {
            if (
                window[`gf_submitting_${this.formID}`] === undefined ||
                !window[`gf_submitting_${this.formID}`]
            ) {
                e.preventDefault();
                e.returnValue = 'Are you sure you want to leave this page?';

                return 'Are you sure you want to leave this page?';
            }
        });
    };

    hideConfirmOnClick = () => {
        const pageID = `gform_page_${this.formID}_${this.lastPage}`;
        const page = document.getElementById(pageID);
        const button = page.querySelector('.gform_button');
        button.addEventListener('click', () => {
            button.classList.add('u-opacity');
        });
    };

    hideFirstNextButton = () => {
        if (this.formID) {
            const pageID = `gform_page_${this.formID}_1`;
            const page = document.getElementById(pageID);
            const button = page.querySelector('.gform_next_button');
            button.style.display = 'none';
        } else {
            const pages = document.querySelectorAll('.gform_page');
            const button = pages[0].querySelector('.gform_next_button');
            button.style.display = 'none';
        }
    };

    // in case the user has come from a clinic that does not offer yellow fever
    yellowFeverSetup = () => {
        const pageID = `gform_page_${this.formID}_${this.yellowFeverPage}`;
        const page = document.getElementById(pageID);
        const button = page.querySelector('.gform-page-footer');
        button.style.display = 'none';

        // if yellow fever is not available
        if (
            this.clinicIsSet &&
            this.selectedClinic &&
            this.selectedClinic.hasYellowFever == false
        ) {
            // hide the unsure option
            const unsureOption = page.querySelector(
                '.ginput_container_radio .gchoice:last-child',
            );
            unsureOption.style.display = 'none';
            // change the text of the question
            const label = page.querySelector('.gfield_label');
            label.innerHTML =
                'This clinic does not offer Yellow Fever vaccinations. Would you like to proceed at this clinic?';
            // change the Yes option to Change clinic (because yes, they do want yellow fever)
            const yesOption = page.querySelector(
                '.ginput_container_radio .gchoice:nth-child(2) label',
            );
            yesOption.innerHTML = 'Select another clinic';
            // change the No option to continue (because no, they don't want yellow fever)
            const noOption = page.querySelector(
                '.ginput_container_radio .gchoice:nth-child(3) label',
            );
            noOption.innerHTML = 'Continue with this clinic';
            const changeClinicOption = page.querySelector(
                '.ginput_container_radio .gchoice:nth-child(2) label',
            );
        }
    };

    // if the user has come from a clinic that does not offer yellow fever but they have said they want it, clear the clinic
    checkYellowFever = () => {
        if (
            this.clinicIsSet &&
            this.selectedClinic &&
            this.selectedClinic.hasYellowFever == false &&
            this.myServices == '2063-'
        ) {
            const pageID = `gform_page_${this.formID}_${this.appointmentsPageNumber}`;
            const page = document.getElementById(pageID);
            // clear the set clinic field so they can get to the map
            const fieldID = `input_${this.formID}_${this.clinicIsSetFieldID}`;
            const field = document.getElementById(fieldID);
            field.value = '';
            this.clinicIsSet = false;
            this.selectedClinic = '';
        }
    };

    // on the appointments page, show their selected clinic
    showHideClinicBox = (page) => {
        if (page === this.appointmentsPageNumber) {
            const text = this.clinicBox.querySelector(
                '[data-behaviour="selected-clinic"]',
            );
            text.innerHTML = this.selectedClinic.title;
            this.clinicBox.classList.add('active');
        } else {
            this.clinicBox.classList.remove('active');
        }
    };

    // if the user clicks to change their clinic
    setupClinicBox = () => {
        this.clinicBox = document.querySelector(
            '[data-behaviour="clinic-box"]',
        );
        const link = this.clinicBox.querySelector('a');
        link.addEventListener('click', (e) => {
            e.preventDefault();
            const pageID = `gform_page_${this.formID}_${this.appointmentsPageNumber}`;
            const page = document.getElementById(pageID);
            // clear the set clinic field so they can get to the map
            const fieldID = `input_${this.formID}_${this.clinicIsSetFieldID}`;
            const field = document.getElementById(fieldID);
            field.value = '';
            this.clinicIsSet = false;
            this.selectedClinic = '';
            // trigger the change page button
            const button = page.querySelector('.gform_previous_button');
            button.click();
        });
    };

    // used to wrap elements on the final "personal details" page so that it can appear in blocks
    wrapElements = (startClass, endClass, newClass) => {
        // create new wrapping divs
        const wrappingElement = document.createElement('div');
        wrappingElement.className = 'content-block-white';
        const bespokeElement = document.createElement('div');
        bespokeElement.className = newClass;
        wrappingElement.appendChild(bespokeElement);
        // move the first element into this wrapping div
        const el = document.querySelector(startClass);
        el.replaceWith(wrappingElement);
        bespokeElement.appendChild(el);
        // loop through 100 times (should cover them all!) to take the next element until the end class is reached
        for (var i = 0; i < 100; i++) {
            let next = wrappingElement.nextElementSibling;
            bespokeElement.appendChild(next);
            if (next.matches(endClass)) {
                return;
            }
        }
    };

    // move the footnote to underneath the buttons
    moveFootnote = () => {
        const page = document.getElementById(
            `gform_page_${this.formID}_${this.instructionsPage}`,
        );
        const visibleLogic = page.querySelector(
            '[data-conditional-logic="visible"]',
        );
        if (!visibleLogic) {
            setTimeout(() => {
                this.moveFootnote();
            }, 500);
            return;
        }
        const footnote = visibleLogic.querySelector('.after-btn-footnote');
        if (footnote) {
            footnote.remove();
            page.querySelector('.gform_page_footer').after(footnote);
        }
    };

    fetchClinicData = (clinicID) => {
        fetch(`/cd/feeds/finder/clinics/id/${clinicID}`)
            .then((response) => response.json())
            .then((data) => {
                if (data.clinic && data.clinic.id) {
                    this.selectedClinic = data.clinic;
                } else {
                    this.failAll();
                }
            })
            .catch((error) => {
                this.failAll();
            });
    };

    // hopefully temporary but just to stop the whole form if a clinic ID was wrong
    failAll = () => {
        const page = document.getElementById('main');
        page.innerHTML =
            'There has been an error with your booking.  Please contact us on 0333 006 9976 and we can process your booking.  Please provide error code 1000.  Alternatively please try again from the home page.';
    };

    triggerAnalytics = (step, name) => {
        this.analytics['step_number'] = step;
        this.analytics['step_name'] = name;

        // get name of selected service
        if (step == 2) {
            const services = document.querySelectorAll(
                '.first-question input:checked, .second-question input:checked',
            );

            services.forEach((opt) => {
                if (opt.value !== 'void') {
                    const labelID = opt.id.replace('choice_', 'label_');
                    const label = document.getElementById(labelID);
                    this.analytics['service'] =
                        label.innerText === 'Yes' || label.innerText === 'No'
                            ? 'Travel Vaccinations'
                            : label.innerText;
                }
            });
            // get name of selected service types
        } else if (step == 4) {
            let services = document.querySelectorAll(
                '.sexual-health input:checked, .generic-vaccinations input:checked',
            );
            let service_type = '';
            services.forEach((opt) => {
                const labelID = opt.id.replace('choice_', 'label_');
                const label = document.getElementById(labelID);
                const separator = service_type !== '' ? '|' : '';
                service_type += `${separator}${label.innerText.trim()}`;
            });
            if (!service_type) {
                services = document.querySelector(
                    '.yellow-fever input:checked',
                );
                if (
                    services &&
                    services.value != 'no' &&
                    services.value != 'unsure' &&
                    services.value != '' &&
                    services.value != 'void'
                ) {
                    service_type = 'Yellow Fever';
                }
            }
            if (service_type) {
                this.analytics['service_type'] = service_type.trim();
            }
            // get destination
        } else if (
            step == 7 &&
            this.analytics['service'] == 'Travel Vaccinations'
        ) {
            const country = document.querySelector('.select-country input');
            this.analytics['destination'] = country.value;
        }

        // if we know the number of people now
        if (
            this.totalPeople &&
            (this.analytics['number_of_patients'] == undefined ||
                this.analytics['number_of_patients'] != this.totalPeople)
        ) {
            this.analytics['number_of_patients'] = this.totalPeople;
        }

        // if we know the selected clinic now
        if (
            this.selectedClinic &&
            (this.analytics['clinic'] == undefined ||
                this.analytics['clinic'] != this.selectedClinic.title)
        ) {
            this.analytics['clinic'] = this.selectedClinic.title;
            this.analytics['type'] = this.selectedClinic.flagship
                ? 'XMED'
                : 'Semble';
        }

        // if we know the appointment now
        if (
            this.selectedAppointment.date &&
            (this.analytics['date'] == undefined ||
                this.analytics['date'] !=
                    this.selectedAppointment.date.substr(0, 10))
        ) {
            this.analytics['date'] = this.selectedAppointment.date.substr(
                0,
                10,
            );
        }

        let data = {};

        data['step_number'] = this.analytics['step_number'];
        data['step_name'] = this.analytics['step_name'];

        // Note: Step 1 doesn't have any additional data to send other than
        // step_number and step_name
        const dataLayerStep2 = {
            service: this.analytics['service'],
        };
        const dataLayerStep3 = {
            service_type: this.analytics['service_type'],
        };

        // Note: Step 4 doesn't have any additional data to send other than
        // step_number, step_name, service and service_type which are already
        // sent in the dataLayerStep2 and dataLayerStep3 payloads.
        const dataLayerStep5 = {
            clinic: this.analytics['clinic'],
            type: this.analytics['type'],
        };
        const dataLayerStep6 = {
            number_of_patients: this.analytics['number_of_patients'],
            date: this.analytics['date'],
        };
        const dataLayerStep7 = {
            destination: this.analytics['destination'],
        };

        if (step >= 2) {
            data = { ...data, ...dataLayerStep2 };
        }

        if (step >= 3) {
            data = {
                ...data,
                ...dataLayerStep3,
            };
        }

        if (step >= 5) {
            data = {
                ...data,
                ...dataLayerStep5,
            };
        }

        if (step >= 6) {
            data = {
                ...data,
                ...dataLayerStep6,
            };
        }

        if (step >= 7) {
            data = {
                ...data,
                ...dataLayerStep7,
            };
        }

        window.dataLayer.push({
            data: data,
            _clear: true,
            event: 'booking_journey',
        });
    };
}
new BookingJourney();
